import {
  Button,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Radio,
  Select,
  Switch,
  TimePicker,
} from "antd";
import TextArea from "antd/es/input/TextArea";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAppSelector } from "../../../app/store";
import AddIcon from "../../../assets/icons/AddIcon";
import PlusOutlineIcon from "../../../assets/icons/PlusOutlineIcon";
import RedDeleteIcon from "../../../assets/icons/RedDeleteIcon";
import RoundEditIcon from "../../../assets/icons/RoundEditIcon";
import SelectDropdownLgIcon from "../../../assets/icons/SelectDropdownLgIcon";
import TrashIcon from "../../../assets/icons/TrashIcon";
import BalanceCard from "../../../components/Admin/BalanceCard";
import CongratulationsModal from "../../../components/common/CongratulationsModal";
import TitleBar from "../../../components/common/TitleBar";
import LocationModal from "../../../components/Organization/LocationModal";
import ConfirmModal from "../../../components/ui/ConfirmModal";
import Loader from "../../../components/ui/Loader";
import { dbDateFormat, dbTimeFormat } from "../../../constants/constants";
import {
  useCreateJobPostMutation,
  useGetSingleJobPostQuery,
  useGetSkillListQuery,
  useUpdateJobPostMutation,
} from "../../../features/organization/job/jobApi";
import { useGetAllSurveyTemplateQuery } from "../../../features/organization/survey/surveyApi";
import useError from "../../../hooks/useError";
import useModal from "../../../hooks/useModal";
import SidebarLayout from "../../../layouts/SidebarLayout";
import { toast } from "../../../lib/toast";
import { selectStringOption } from "../../../utils/utils";
import CalanderIcon from "../survey/templates/_icons/CalanderIcon";
import InputLinkIcon from "./_icons/InputLinkIcon";

type ModalType = "CONFIRM" | "LOCATION" | "CONGRATULATION";
const backLink = "/organization/job-list";

export default function ManageJob({ type }: { type?: "view" }) {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAppSelector((state) => state.auth);
  const { open, handleOpen, handleClose } = useModal<unknown, ModalType>(
    reRender
  );
  const [selectedLocationIndex, setSelectedLocationIndex] = useState<
    number | null
  >(null);
  const [copied, setCopied] = useState(false);
  const selectedContractType = Form.useWatch("contract_type", form);
  const additionalContentsType = Form.useWatch(
    "specific_location_or_number_of_bondhu",
    form
  );
  const selectedCategory = Form.useWatch("job_category", form);
  const fixed_term_duration = Form.useWatch("fixed_term_duration", form) || 0;
  const fixed_term_price_per_month =
    Form.useWatch("fixed_term_price_per_month", form) || 0;
  const hourly_price = Form.useWatch("hourly_price", form) || 0;
  const open_bid_max_budget = Form.useWatch("open_bid_max_budget", form) || 0;
  const milestone_price = Form.useWatch("milestone_price", form) || 0;
  const [maxBudget, setMaxBudget] = useState(0);
  const [createdJobData, setCreatedJobData] = useState<any | null>(null);

  useEffect(() => {
    const fixedTermPrice = fixed_term_duration * fixed_term_price_per_month;

    if (selectedContractType === "fixed_term") {
      setMaxBudget(fixedTermPrice);
    } else if (selectedContractType === "hourly") {
      setMaxBudget(hourly_price);
    } else if (selectedContractType === "open_bid") {
      setMaxBudget(open_bid_max_budget);
    } else if (selectedContractType === "milestone") {
      setMaxBudget(milestone_price);
    } else {
      setMaxBudget(0);
    }
  }, [
    fixed_term_duration,
    fixed_term_price_per_month,
    hourly_price,
    milestone_price,
    open_bid_max_budget,
    selectedContractType,
  ]);

  const {
    data: getSkillData,
    isError: isSkillError,
    error: skillError,
    isLoading: isSkillLoading,
  } = useGetSkillListQuery({
    params: {
      business_id: user?.business_id,
    },
  });
  const {
    data: getAllSurveyTemplateData,
    isError: getAllSurveyTemplateIsError,
    error: getAllSurveyTemplateError,
  } = useGetAllSurveyTemplateQuery({
    params: {
      business_id: user?.business_id,
      status: "active",
    },
  });

  const [
    createJobPost,
    {
      isLoading: createJobPostIsLoading,
      isError: createJobPostIsError,
      error: createJobPostError,
      isSuccess: createJobPostIsSuccess,
    },
  ] = useCreateJobPostMutation();

  const {
    data: getSingleJobPostData,
    isLoading: getSingleJobPostIsLoading,
    isError: getSingleJobPostIsError,
    error: getSingleJobPostError,
  } = useGetSingleJobPostQuery(Number(id), {
    skip: !id,
  });

  const [
    updateJobPost,
    {
      isLoading: updateJobPostIsLoading,
      isError: updateJobPostIsError,
      error: updateJobPostError,
      isSuccess: updateJobPostIsSuccess,
    },
  ] = useUpdateJobPostMutation();

  useError(
    getAllSurveyTemplateIsError,
    getAllSurveyTemplateError,
    "Get survey template fail"
  );
  useError(createJobPostIsError, createJobPostError, "Job post create fail");
  useError(
    getSingleJobPostIsError,
    getSingleJobPostError,
    "Get single job data fail"
  );
  useError(updateJobPostIsError, updateJobPostError, "Job post updated fail");
  useError(isSkillError, skillError, "Skill get fail");

  const specific_location = Form.useWatch("specific_location", form) || [];
  const number_of_bondhu = Form.useWatch("number_of_bondhu", form);
  const totalLocationBondhu = specific_location?.reduce(
    (acc: any, curr: any) => acc + Number(curr?.number_of_bondhu || 0),
    0
  );

  const survey_location = Form.useWatch("survey_location", form) || [];
  const totalSurveyLocation = survey_location?.reduce(
    (acc: any, curr: any) => acc + Number(curr?.number_of_bondhu || 0) || 0,
    0
  );

  const selectedBondhu =
    additionalContentsType === "specific_location"
      ? totalLocationBondhu || 0
      : additionalContentsType === "number_of_bondhu"
      ? Number(number_of_bondhu || 0)
      : 0;

  const onFinish = (values: any) => {
    // if (totalSurveyLocation > selectedBondhu) {
    //   toast(
    //     "error",
    //     "Survey Location Bondhu cannot be greater than selected Bondhu"
    //   );
    //   return;
    // }

    if (selectedCategory === "survey" || selectedCategory === "kyc") {
      if (totalSurveyLocation !== selectedBondhu) {
        toast(
          "error",
          "Total Bondhu can’t exceed the number set for this location"
        );
        return;
      }
    }

    const newValues = {
      ...values,
      fixed_term_duration: values?.fixed_term_duration
        ? String(values?.fixed_term_duration)
        : undefined,
      fixed_term_price_per_month: values?.fixed_term_price_per_month
        ? String(values?.fixed_term_price_per_month)
        : undefined,
      hourly_price: values?.hourly_price
        ? String(values?.hourly_price)
        : undefined,
      open_bid_max_budget: values?.open_bid_max_budget
        ? String(values?.open_bid_max_budget)
        : undefined,
      milestone_price: values?.milestone_price
        ? String(values?.milestone_price)
        : undefined,
      max_budget: values?.max_budget ? String(values?.max_budget) : undefined,
      slots: {
        main_slots: modifySlots(values?.main_slots),
        additional_slots: modifySlots(values?.additional_slots),
      },
      main_slots: undefined,
      additional_slots: undefined,
      is_allow_file_upload: values?.is_allow_file_upload ? 1 : 0,
    };

    const newCallCenterValues = {
      job_title: values?.job_title,
      visibility: values?.visibility,
      job_end_date: values?.job_end_date
        ? dayjs(values?.job_end_date).format("YYYY-MM-DD")
        : null,
      joining_date: values?.joining_date
        ? dayjs(values?.joining_date).format("YYYY-MM-DD")
        : null,
      job_category: values?.job_category,
      job_description: values?.job_description,
      penalty_clause: values?.penalty_clause,
      skills: values?.skills,
      additional_questions: values?.additional_questions,
      specific_location: values?.specific_location,
      number_of_bondhu: values?.specific_location?.reduce(
        (sum: number, item: any) => sum + Number(item.number_of_bondhu || 0),
        0
      ),
      specific_location_or_number_of_bondhu:
        values?.specific_location_or_number_of_bondhu,
    };

    if (selectedCategory === "call_center") {
      handleOpen({
        type: "CONFIRM",
        data: newCallCenterValues,
      });
    } else {
      handleOpen({
        type: "CONFIRM",
        data: newValues,
      });
    }
  };

  function modifySlots(slots: any[]) {
    if (slots?.length) {
      return slots?.map((el: any) => {
        const newSlots = {
          start_date: el?.dateRange?.[0]?.format(dbDateFormat),
          end_date: el?.dateRange?.[1]?.format(dbDateFormat),
          dateRange: undefined,

          slots: el?.slots?.length
            ? el?.slots?.map((item: any) => {
                return {
                  ...item,
                  startTime: item?.startTime?.format(dbTimeFormat),
                  endTime: item?.endTime?.format(dbTimeFormat),
                  is_seat_active: item?.is_seat_active || false,
                };
              })
            : undefined,
        };

        return newSlots;
      });
    }

    return undefined;
  }

  const additionalContents = [
    ...(selectedCategory !== "remote_work"
      ? [
          {
            label: "Specific Location",
            value: "specific_location",
            description: "",
            // description:
            //   "Project is broken into phases/milestones Payment released upon milestone delivery",
          },
        ]
      : []),
    ...(selectedCategory !== "rooster" && selectedCategory !== "call_center"
      ? [
          {
            label: "Number of Bondhu",
            value: "number_of_bondhu",
            // description:
            //   "Project is broken into phases/milestones Payment released upon milestone delivery",
          },
        ]
      : []),
  ];

  const renderContractItems = (type: string, viewType?: "view") => {
    if (!selectedContractType) return null;

    if (selectedContractType === "fixed_term" && type === "fixed_term") {
      return (
        <div className="flex gap-3">
          <Form.Item
            className="mb-2"
            name={"fixed_term_duration"}
            label="Month"
            validateFirst
            rules={[
              {
                required: true,
                message: "Please enter duration in months",
              },
              {
                validator(_, value) {
                  if (value === undefined || value === null || value === "") {
                    return Promise.resolve();
                  }

                  const num = Number(value);

                  if (isNaN(num) || !Number.isInteger(num)) {
                    return Promise.reject(
                      new Error("Only integer number are allowed")
                    );
                  }

                  if (num <= 0) {
                    return Promise.reject(
                      new Error("Value must be a positive number")
                    );
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              placeholder="Type month"
              // min={1}
              // step={1}
              style={{ minWidth: 150 }}
              disabled={viewType === "view"}
            />
          </Form.Item>
          <Form.Item
            className="mb-2"
            name="fixed_term_price_per_month"
            label="Set a Price/Month"
            validateFirst
            rules={[
              // {
              //   required: true,
              //   message: "Please enter duration in months",
              // },
              {
                validator(_, value) {
                  if (value === undefined || value === null || value === "") {
                    return Promise.resolve();
                  }

                  const num = Number(value);

                  // isNaN(num) || !Number.isInteger(num)
                  if (isNaN(num)) {
                    return Promise.reject(
                      new Error("Only numbers are allowed")
                    );
                  }

                  if (num <= 0) {
                    return Promise.reject(
                      new Error("Value must be a positive number")
                    );
                  }

                  const regex = /^\d+(\.\d{1,2})?$/;
                  if (!regex.test(value)) {
                    return Promise.reject(
                      new Error("Only up to 2 decimal places allowed")
                    );
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              className="min-w-[200px]"
              placeholder="000"
              size="large"
              disabled={viewType === "view"}
            />
          </Form.Item>
        </div>
      );
    } else if (selectedContractType === "hourly" && type === "hourly") {
      return (
        <Form.Item
          rules={[
            // {
            //   required: true,
            //   message: "Please enter duration in months",
            // },
            {
              validator(_, value) {
                if (value === undefined || value === null || value === "") {
                  return Promise.resolve();
                }

                const num = Number(value);

                // isNaN(num) || !Number.isInteger(num)
                if (isNaN(num)) {
                  return Promise.reject(new Error("Only numbers are allowed"));
                }

                if (num <= 0) {
                  return Promise.reject(
                    new Error("Value must be a positive number")
                  );
                }

                const regex = /^\d+(\.\d{1,2})?$/;
                if (!regex.test(value)) {
                  return Promise.reject(
                    new Error("Only up to 2 decimal places allowed")
                  );
                }

                return Promise.resolve();
              },
            },
          ]}
          className="mb-2"
          name="hourly_price"
          label="Price"
        >
          <Input
            className="min-w-[200px]"
            placeholder="000"
            size="large"
            disabled={viewType === "view"}
          />
        </Form.Item>
      );
    } else if (selectedContractType === "open_bid" && type === "open_bid") {
      return (
        <Form.Item
          rules={[
            // {
            //   required: true,
            //   message: "Please enter duration in months",
            // },
            {
              validator(_, value) {
                if (value === undefined || value === null || value === "") {
                  return Promise.resolve();
                }

                const num = Number(value);

                // isNaN(num) || !Number.isInteger(num)
                if (isNaN(num)) {
                  return Promise.reject(new Error("Only numbers are allowed"));
                }

                if (num <= 0) {
                  return Promise.reject(
                    new Error("Value must be a positive number")
                  );
                }

                if (num > 1000000000) {
                  return Promise.reject(
                    new Error("Budget cannot exceed 1000000000")
                  );
                }

                const regex = /^\d+(\.\d{1,2})?$/;
                if (!regex.test(value)) {
                  return Promise.reject(
                    new Error("Only up to 2 decimal places allowed")
                  );
                }

                return Promise.resolve();
              },
            },
          ]}
          className="mb-2"
          name="open_bid_max_budget"
          label="Budget"
        >
          <Input
            className="min-w-[200px]"
            placeholder="000"
            size="large"
            disabled={viewType === "view"}
          />
        </Form.Item>
      );
    } else if (selectedContractType === "milestone" && type === "milestone") {
      return (
        <div className="flex gap-3">
          <Form.Item
            className="mb-2"
            name="milestone_count"
            label="Phases/Milestones"
            rules={[
              // {
              //   required: true,
              //   message: "Please enter duration in months",
              // },
              {
                validator(_, value) {
                  if (value === undefined || value === null || value === "") {
                    return Promise.resolve();
                  }

                  const num = Number(value);

                  if (isNaN(num) || !Number.isInteger(num)) {
                    return Promise.reject(
                      new Error("Only integer number are allowed")
                    );
                  }

                  if (num <= 0) {
                    return Promise.reject(
                      new Error("Value must be a positive number")
                    );
                  }

                  return Promise.resolve();
                },
              },
            ]}
          >
            <Input
              placeholder="Type Milestones"
              min={1}
              step={1}
              style={{ minWidth: 200 }}
              disabled={viewType === "view"}
            />
          </Form.Item>
          <Form.Item
            rules={[
              // {
              //   required: true,
              //   message: "Please enter duration in months",
              // },
              {
                validator(_, value) {
                  if (value === undefined || value === null || value === "") {
                    return Promise.resolve();
                  }

                  const num = Number(value);

                  if (isNaN(num) || !Number.isInteger(num)) {
                    return Promise.reject(
                      new Error("Only integer number are allowed")
                    );
                  }

                  if (num <= 0) {
                    return Promise.reject(
                      new Error("Value must be a positive number")
                    );
                  }

                  return Promise.resolve();
                },
              },
            ]}
            className="mb-2"
            name="milestone_price"
            label="Price per Milestones"
          >
            <InputNumber
              className="min-w-[200px]"
              placeholder="000"
              size="large"
              disabled={viewType === "view"}
            />
          </Form.Item>
        </div>
      );
    }
  };

  const renderAdditionalContents = (type: string, viewType?: "view") => {
    if (
      additionalContentsType === "specific_location" &&
      type === "specific_location"
    ) {
      return (
        <div>
          <Form.List name="specific_location" initialValue={[{}]}>
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }, index) => (
                  <>
                    <div className="flex items-start gap-3">
                      <Form.Item
                        rules={[
                          {
                            required: true,
                            message: "Please enter a location",
                          },
                        ]}
                        className="mb-3"
                        name={[name, "location"]}
                        label="Add Location"
                      >
                        <Input
                          className="min-w-[200px]"
                          placeholder="Select"
                          size="large"
                          disabled={viewType === "view"}
                        />
                      </Form.Item>

                      <Form.Item
                        rules={[
                          {
                            required: true,
                            message: "Please select number of bondhu",
                          },

                          {
                            validator(_, value) {
                              if (
                                value === undefined ||
                                value === null ||
                                value === ""
                              ) {
                                return Promise.resolve();
                              }

                              const num = Number(value);

                              if (isNaN(num) || !Number.isInteger(num)) {
                                return Promise.reject(
                                  new Error("Only integer number are alloweds")
                                );
                              }

                              if (num <= 0) {
                                return Promise.reject(
                                  new Error("Value must be a positive number")
                                );
                              }

                              return Promise.resolve();
                            },
                          },
                        ]}
                        className="mb-2"
                        name={[name, "number_of_bondhu"]}
                        label="Number of Bondhu"
                      >
                        <Input
                          className="min-w-[200px]"
                          placeholder="#"
                          size="large"
                          disabled={viewType === "view"}
                        />
                      </Form.Item>

                      {fields.length > 1 && viewType !== "view" && (
                        <div
                          className="cursor-pointer mt-10"
                          onClick={() => remove(name)}
                        >
                          <RedDeleteIcon />
                        </div>
                      )}
                    </div>
                  </>
                ))}

                {viewType !== "view" && (
                  <button
                    type="button"
                    className="flex items-center gap-2 text-link text-sm font-medium"
                    onClick={() => add()}
                  >
                    <PlusOutlineIcon />
                    Another Location
                  </button>
                )}
              </>
            )}
          </Form.List>
        </div>
      );
    } else if (
      additionalContentsType === "number_of_bondhu" &&
      type === "number_of_bondhu" &&
      selectedCategory !== "rooster"
    ) {
      return (
        <Form.Item
          rules={[
            {
              required: true,
              message: "Please select number of bondhu",
            },
            {
              validator(_, value) {
                if (value === undefined || value === null || value === "") {
                  return Promise.resolve();
                }

                const num = Number(value);

                if (isNaN(num) || !Number.isInteger(num)) {
                  return Promise.reject(
                    new Error("Only integer number are allowed")
                  );
                }

                if (num <= 0) {
                  return Promise.reject(
                    new Error("Value must be a positive number")
                  );
                }

                return Promise.resolve();
              },
            },
          ]}
          className="mb-2"
          name="number_of_bondhu"
          label="Number"
        >
          <Input
            className="min-w-[200px]"
            placeholder="#"
            size="large"
            disabled={viewType === "view"}
          />
        </Form.Item>
      );
    }
  };

  function reRender() {
    navigate(backLink);
  }

  useEffect(() => {
    if (id && getSingleJobPostData?.data) {
      const data = getSingleJobPostData?.data?.job;

      form.setFieldsValue({
        job_title: data?.job_title,
        job_category: data?.job_category,
        job_description: data?.job_description,
        penalty_clause: data?.penalty_clause,
        skills: data?.skills,
        fixed_term_duration: data?.fixed_term_duration
          ? Number(data?.fixed_term_duration)
          : undefined,
        fixed_term_price_per_month: data?.fixed_term_price_per_month
          ? Number(data?.fixed_term_price_per_month)
          : undefined,
        hourly_price: data?.hourly_price
          ? Number(data?.hourly_price)
          : undefined,
        open_bid_max_budget: data?.open_bid_max_budget
          ? Number(data?.open_bid_max_budget)
          : undefined,
        milestone_count: data?.milestone_count,
        milestone_price: data?.milestone_price
          ? Number(data?.milestone_price)
          : undefined,
        specific_location: Array.isArray(data?.specific_location)
          ? data.specific_location.map((item) => ({
              location: item.location,
              number_of_bondhu: item.number_of_bondhu,
            }))
          : [],
        number_of_bondhu: data?.number_of_bondhu,
        max_budget: data?.max_budget ? Number(data?.max_budget) : undefined,
        additional_questions: data?.additional_questions,
        survey_template_id: data?.survey_template_id,
        survey_location: Array.isArray(data?.survey_location)
          ? data?.survey_location.map((loc) => ({
              location: loc.location,
              number_of_bondhu: loc.number_of_bondhu,
              radius: loc.radius,
            }))
          : [],
        tags: data?.tags,
        contract_type: data?.contract_type,
        specific_location_or_number_of_bondhu:
          data?.specific_location_or_number_of_bondhu,
        main_slots: modifySlotsForGet(data?.slots?.main_slots),
        additional_slots: modifySlotsForGet(data?.slots?.additional_slots),
        is_allow_file_upload: data?.is_allow_file_upload ? true : false,
        max_number_of_files: data?.max_number_of_files || undefined,
        // Call Center
        visibility: data?.visibility,
        job_end_date: data?.job_end_date ? dayjs(data?.job_end_date) : null,
        joining_date: data?.joining_date ? dayjs(data?.joining_date) : null,
      });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, getSingleJobPostData?.data, form]);

  function modifySlotsForGet(slots: any[]) {
    if (slots?.length) {
      return slots?.map((el: any) => {
        const newSlots = {
          start_date: el?.dateRange?.[0]?.format(dbDateFormat),
          end_date: el?.dateRange?.[1]?.format(dbDateFormat),
          dateRange: [
            dayjs(el?.start_date, dbDateFormat),
            dayjs(el?.end_date, dbDateFormat),
          ],

          slots: el?.slots?.length
            ? el?.slots?.map((item: any) => {
                return {
                  ...item,
                  startTime: dayjs(item?.startTime, dbTimeFormat),
                  endTime: dayjs(item?.endTime, dbTimeFormat),
                  is_seat_active: item?.is_seat_active || false,
                };
              })
            : undefined,
        };

        return newSlots;
      });
    }

    return undefined;
  }

  // useEffect(() => {
  //   form.setFieldsValue({
  //     main_slots: [
  //       {
  //         main_time_slots: [{}],
  //       },
  //     ],
  //   });
  // }, [form]);

  // useEffect(() => {
  //   form.setFieldsValue({
  //     aditional_slots: [
  //       {
  //         aditional_time_slots: [{}],
  //       },
  //     ],
  //   });
  // }, [form]);

  // useEffect(() => {
  //   form.setFieldsValue({
  //     survey_location: [{}],
  //   });
  // }, [form]);

  // useEffect(() => {
  //   form.setFieldsValue({
  //     specific_location: [{}],
  //   });
  // }, [form]);

  const domainURL = window.location.origin;
  const link = `${domainURL}/bondhu-cms/v1/job/v1/details/${createdJobData?.data?.job?.id}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCongratClose = () => {
    handleClose(true);
    setCreatedJobData(null);
  };

  useEffect(() => {
    if (createJobPostIsSuccess) {
      handleOpen({ type: "CONGRATULATION" });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createJobPostIsSuccess]);

  useEffect(() => {
    if (updateJobPostIsSuccess) {
      handleOpen({ type: "CONGRATULATION" });
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateJobPostIsSuccess]);

  return (
    <SidebarLayout
      title={type === "view" ? "View Job" : "Post a Job"}
      contentClass="manage-job"
    >
      <TitleBar
        title={type === "view" ? "View Job" : "Post a Job"}
        backLink={backLink}
      />

      {open?.type === "LOCATION" && type !== "view" && (
        <LocationModal
          handleClose={handleClose}
          open={open?.open}
          data={open?.data}
          // handleManage={(values) => {
          //   console.log(values);
          // }}
          handleManage={(values) => {
            const currentList = form.getFieldValue("survey_location") || [];
            if (
              selectedLocationIndex !== null &&
              selectedLocationIndex !== undefined
            ) {
              currentList[selectedLocationIndex] = {
                ...currentList[selectedLocationIndex],
                location: values.office_name,
                radius: values.radius,
              };
              form.setFieldsValue({
                survey_location: currentList,
              });
            }
            handleClose();
          }}
        />
      )}

      {/* {open?.type === "CONFIRM" && (
        <ConfirmModal
          title={
            id
              ? "Do you want to update this job?"
              : "Do you want to post this job?"
          }
          subTitle={
            id
              ? "Are you ready to update this job and make it visible to Bondhu?"
              : "Are you ready to publish this job and make it visible to Bondhu?"
          }
          width={450}
          handleClose={() => handleClose()}
          open={open?.open}
          data={open?.data}
          okLoading={createJobPostIsLoading || updateJobPostIsLoading}
          handleOk={(values) => {
            // console.log("values:", values);
            if (id) {
              const newData = {
                id: id,
                data: values,
              };
              updateJobPost(newData);
            } else {
              createJobPost(values);
            }
          }}
          okText={id ? "Yes, Update" : "Yes, Post"}
        />
      )} */}

      {open?.type === "CONFIRM" && (
        <ConfirmModal
          title={
            id
              ? "Do you want to update this job?"
              : "Do you want to post this job?"
          }
          subTitle={
            id
              ? "Are you ready to update this job and make it visible to Bondhu?"
              : "Are you ready to publish this job and make it visible to Bondhu?"
          }
          width={450}
          handleClose={() => handleClose()}
          open={open?.open}
          data={open?.data}
          okLoading={createJobPostIsLoading || updateJobPostIsLoading}
          handleOk={(values) => {
            if (id) {
              // UPDATE
              const newData = {
                id,
                data: values,
              };

              updateJobPost(newData)
                .unwrap()
                .then((res) => {
                  setCreatedJobData(res); // Save update response
                })
                .catch((err) => {});
            } else {
              // CREATE
              createJobPost(values)
                .unwrap()
                .then((res) => {
                  setCreatedJobData(res); // SAVE RESPONSE HERE
                })
                .catch((err) => {});
            }
          }}
          okText={id ? "Yes, Update" : "Yes, Post"}
        />
      )}

      {open?.type === "CONGRATULATION" && (
        <CongratulationsModal
          open={open?.open}
          handleClose={() => handleCongratClose()}
          // text={
          //   id
          //     ? "Your updated job post is almost live! You’re one step away from hiring awesome talent."
          //     : "Your first job post is almost live! You’re one step away from hiring awesome talent."
          // }
          text={`${
            createdJobData?.data?.job?.visibility === "private"
              ? "Your job has been posted privately. A unique link has been generated for you to share with selected Bondhus. They can access and apply using this link only"
              : "Your job post is live! You’re one step away from hiring awesome talent."
          }`}
        >
          {createdJobData?.data?.job?.visibility === "private" ? (
            <div className="flex items-center bg-white border border-slate-400 rounded-lg p-2 w-full max-w-xl mb-5">
              {/* Icon */}
              <div className="px-2 text-gray-500">
                <InputLinkIcon />
              </div>

              {/* Input (readonly) */}
              <Input
                value={link}
                readOnly
                className="!border-none !shadow-none bg-transparent text-[#8897AE] font-medium truncate"
              />

              {/* Copy button */}
              <Button
                type="default"
                onClick={handleCopy}
                className="!bg-fuchsia-600 hover:!bg-fuchsia-600 !text-white"
              >
                {copied ? "Copied!" : "Copy Link"}
              </Button>
            </div>
          ) : null}
        </CongratulationsModal>
      )}

      {getSingleJobPostIsLoading ? (
        <Loader />
      ) : (
        <Form
          className="custom-form"
          layout="vertical"
          requiredMark="optional"
          size="large"
          onFinish={onFinish}
          form={form}
          initialValues={{
            // job_category: "survey",
            survey_location: [{}],
          }}
        >
          <div className="grid md:grid-cols-3 gap-[18px]">
            <div className="p-4 bg-white rounded-lg md:col-span-2">
              <Form.Item
                name="job_title"
                label="Job Title"
                validateFirst
                className="mb-4"
                rules={[
                  {
                    required: true,
                    message: "Please enter job title",
                  },
                  {
                    max: 200,
                    message: "Job title cannot exceed 200 characters",
                  },
                ]}
              >
                <Input
                  size="large"
                  placeholder="Enter job title"
                  disabled={type === "view"}
                />
              </Form.Item>

              <Form.Item
                name="job_category"
                label="Job Category"
                className="mb-4"
                rules={[
                  {
                    required: true,
                    message: "Please select a category",
                  },
                ]}
              >
                <Select
                  placeholder="Select"
                  size="large"
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  options={job_category}
                  suffixIcon={<SelectDropdownLgIcon />}
                  disabled={type === "view"}
                />
              </Form.Item>

              {selectedCategory === "call_center" && (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                  <Form.Item
                    name="visibility"
                    label="Job Visibility"
                    className="mb-4"
                    rules={[
                      {
                        required: true,
                        message: "Please select a visibility",
                      },
                    ]}
                  >
                    <Select
                      placeholder="Select"
                      size="large"
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                      options={job_visibility}
                      suffixIcon={<SelectDropdownLgIcon />}
                      disabled={type === "view"}
                    />
                  </Form.Item>
                  <Form.Item
                    name="job_end_date"
                    label="Job End Date"
                    className="mb-4"
                    rules={[
                      {
                        required: true,
                        message: "Please select joining date",
                      },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const jobEndDate = getFieldValue("job_end_date");
                          if (!value || !jobEndDate) {
                            return Promise.resolve();
                          }
                          if (dayjs(value).isAfter(dayjs(jobEndDate))) {
                            return Promise.reject(
                              new Error(
                                "Job End Date previous date not select for Joining Date"
                              )
                            );
                          }
                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <DatePicker
                      size="large"
                      className="w-full"
                      placeholder="Select"
                      disabled={type === "view"}
                      suffixIcon={<CalanderIcon />}
                      disabledDate={(current) =>
                        current && current < dayjs().startOf("day")
                      }
                    />
                  </Form.Item>
                  <Form.Item
                    name="joining_date"
                    label="Joining Date"
                    className="mb-4"
                    dependencies={["job_end_date"]}
                    rules={[
                      {
                        required: true,
                        message: "Please select joining date",
                      },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          const jobEndDate = getFieldValue("job_end_date");
                          if (!value || !jobEndDate) {
                            return Promise.resolve();
                          }

                          // ❗ Joining date should be same or after job end date
                          if (dayjs(value).isBefore(dayjs(jobEndDate), "day")) {
                            return Promise.reject(
                              new Error(
                                "Job End Date previous date not select for Joining Date"
                              )
                            );
                          }

                          return Promise.resolve();
                        },
                      }),
                    ]}
                  >
                    <DatePicker
                      size="large"
                      className="w-full"
                      placeholder="Select"
                      disabled={type === "view"}
                      suffixIcon={<CalanderIcon />}
                      disabledDate={(current) =>
                        current && current < dayjs().startOf("day")
                      }
                    />
                  </Form.Item>
                </div>
              )}

              <Form.Item
                name="job_description"
                label="Job Description"
                className="mb-4"
                validateFirst
                rules={[
                  {
                    required: true,
                    message: "Please enter description",
                  },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();

                      // Split by spaces and filter out empty strings
                      const words = value.trim().split(/\s+/).filter(Boolean);

                      if (words.length > 400) {
                        return Promise.reject(
                          new Error("Description cannot exceed 400 words")
                        );
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <TextArea
                  placeholder="Enter a description..."
                  rows={4}
                  disabled={type === "view"}
                />
              </Form.Item>

              <div className="text-body-text text-sm font-normal mb-4">
                Info that helps a user with this field.
              </div>

              <Form.Item
                name="penalty_clause"
                label="Penalty Clause"
                className="mb-4"
                validateFirst
                rules={[
                  {
                    required: true,
                    message: "Please enter penalty clause",
                  },
                  {
                    validator: (_, value) => {
                      if (!value) return Promise.resolve();

                      // Split by spaces and filter out empty strings
                      const words = value.trim().split(/\s+/).filter(Boolean);

                      if (words.length > 400) {
                        return Promise.reject(
                          new Error("Penalty clause cannot exceed 400 words")
                        );
                      }

                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <TextArea
                  placeholder="Enter a penalty clause"
                  rows={4}
                  disabled={type === "view"}
                />
              </Form.Item>

              <Form.Item
                name="skills"
                label="Required Skills"
                className="mb-4"
                rules={[
                  {
                    required: true,
                    message: "Please select skills",
                  },
                ]}
              >
                <Select
                  mode="tags"
                  placeholder="Select"
                  size="large"
                  allowClear
                  showSearch
                  options={selectStringOption(getSkillData?.data?.skills || [])}
                  loading={isSkillLoading}
                  suffixIcon={<SelectDropdownLgIcon />}
                  disabled={type === "view"}
                />
              </Form.Item>

              {selectedCategory !== "call_center" && (
                <Divider className="bg-[#D7DFE9]" />
              )}
              {selectedCategory === "call_center" ? null : selectedCategory ===
                "rooster" ? (
                <>
                  <div>
                    <Form.List name="main_slots" initialValue={[{}]}>
                      {(fields, { add, remove }) => (
                        <>
                          <div className="flex justify-between items-center">
                            <div className="justify-start text-zinc-700 text-base font-semibold">
                              Main Slots
                            </div>
                            {type !== "view" && (
                              <button
                                type="button"
                                onClick={() => add({ main_time_slots: [{}] })}
                                className="flex items-center gap-2 text-accent text-sm font-medium"
                              >
                                <PlusOutlineIcon />
                                New Date
                              </button>
                            )}
                          </div>

                          {fields.map(({ key, name, ...restField }, index) => (
                            <div
                              key={key}
                              className="flex justify-start items-start gap-3 mt-3"
                            >
                              <div className="grid grid-cols-10 gap-2 mt-3">
                                <Form.Item
                                  {...restField}
                                  className="mb-0 col-span-3"
                                  name={[name, "dateRange"]}
                                  label="Select Date"
                                  rules={[
                                    {
                                      required: true,
                                      message: "Please select date range",
                                    },
                                  ]}
                                >
                                  <DatePicker.RangePicker
                                    disabled={type === "view"}
                                    format={"DD MMM"}
                                    placeholder={["Start", "End"]}
                                  />
                                </Form.Item>

                                <div className="col-span-7">
                                  <Form.List
                                    name={[name, "slots"]}
                                    initialValue={[{}]}
                                  >
                                    {(
                                      timeFields,
                                      { add: addTime, remove: removeTime }
                                    ) => (
                                      <>
                                        {timeFields.map(
                                          ({
                                            key: timeKey,
                                            name: timeName,
                                            ...timeRestField
                                          }) => (
                                            <div
                                              key={timeKey}
                                              className="grid grid-cols-7 gap-2"
                                            >
                                              <Form.Item
                                                {...timeRestField}
                                                name={[timeName, "startTime"]}
                                                label="Start"
                                                rules={[
                                                  {
                                                    required: true,
                                                    message:
                                                      "Please select start time",
                                                  },
                                                ]}
                                                className="col-span-2 mb-0"
                                              >
                                                <TimePicker
                                                  use12Hours
                                                  format="h:mm a"
                                                  disabled={type === "view"}
                                                />
                                              </Form.Item>

                                              <Form.Item
                                                {...timeRestField}
                                                name={[timeName, "endTime"]}
                                                label="End"
                                                rules={[
                                                  {
                                                    required: true,
                                                    message:
                                                      "Please select end time",
                                                  },
                                                ]}
                                                className="col-span-2 mb-0"
                                              >
                                                <TimePicker
                                                  use12Hours
                                                  format="h:mm a"
                                                  disabled={type === "view"}
                                                />
                                              </Form.Item>

                                              <Form.Item
                                                {...timeRestField}
                                                name={[timeName, "price"]}
                                                label="Price"
                                                className=" mb-0"
                                                rules={[
                                                  {
                                                    required: true,
                                                    message:
                                                      "Please enter price",
                                                  },
                                                ]}
                                              >
                                                <InputNumber
                                                  style={{ width: "100%" }}
                                                  placeholder="৳"
                                                  disabled={type === "view"}
                                                />
                                              </Form.Item>

                                              <MainSeat
                                                form={form}
                                                mainName="main_slots"
                                                mainIndex={name}
                                                timeName="slots"
                                                timeIndex={timeName}
                                                timeRestField={timeRestField}
                                                type={type}
                                              />

                                              {timeFields.length > 1 &&
                                                type !== "view" && (
                                                  <div
                                                    onClick={() =>
                                                      removeTime(timeName)
                                                    }
                                                    className="my-auto"
                                                  >
                                                    <RedDeleteIcon />
                                                  </div>
                                                )}
                                            </div>
                                          )
                                        )}

                                        {type !== "view" && (
                                          <button
                                            type="button"
                                            onClick={() => addTime()}
                                            className="flex items-center gap-2 text-accent text-sm font-medium mt-2"
                                          >
                                            <PlusOutlineIcon /> Another Slot
                                          </button>
                                        )}
                                      </>
                                    )}
                                  </Form.List>
                                </div>
                              </div>

                              {/* Show delete icon only if it's NOT the first item */}
                              {index !== 0 && type !== "view" && (
                                <div
                                  onClick={() => remove(name)}
                                  className="cursor-pointer mt-10"
                                >
                                  <RedDeleteIcon />
                                </div>
                              )}
                            </div>
                          ))}
                        </>
                      )}
                    </Form.List>
                  </div>

                  <div className="mt-2">
                    <Form.List name="additional_slots" initialValue={[{}]}>
                      {(fields, { add, remove }) => (
                        <>
                          <div className="flex justify-between items-center">
                            <div className="justify-start text-zinc-700 text-base font-semibold">
                              Additional Slots
                            </div>
                            {type !== "view" && (
                              <button
                                type="button"
                                onClick={() => add({ main_time_slots: [{}] })}
                                className="flex items-center gap-2 text-accent text-sm font-medium"
                              >
                                <PlusOutlineIcon />
                                New Date
                              </button>
                            )}
                          </div>

                          {fields.map(({ key, name, ...restField }, index) => (
                            <div
                              key={key}
                              className="flex justify-start items-start gap-3 mt-3"
                            >
                              <div className="grid grid-cols-10 gap-2 mt-3">
                                <Form.Item
                                  {...restField}
                                  className="mb-0 col-span-3"
                                  name={[name, "dateRange"]}
                                  label="Select Date"
                                  rules={[
                                    {
                                      required: true,
                                      message: "Please select date range",
                                    },
                                  ]}
                                >
                                  <DatePicker.RangePicker
                                    disabled={type === "view"}
                                    format={"DD MMM"}
                                    placeholder={["Start", "End"]}
                                  />
                                </Form.Item>

                                <div className="col-span-7">
                                  <Form.List
                                    name={[name, "slots"]}
                                    initialValue={[{}]}
                                  >
                                    {(
                                      timeFields,
                                      { add: addTime, remove: removeTime }
                                    ) => (
                                      <>
                                        {timeFields.map(
                                          ({
                                            key: timeKey,
                                            name: timeName,
                                            ...timeRestField
                                          }) => (
                                            <div
                                              key={timeKey}
                                              className="grid grid-cols-7 gap-2"
                                            >
                                              <Form.Item
                                                {...timeRestField}
                                                name={[timeName, "startTime"]}
                                                label="Start"
                                                rules={[
                                                  {
                                                    required: true,
                                                    message:
                                                      "Please select start time",
                                                  },
                                                ]}
                                                className="col-span-2 mb-0"
                                              >
                                                <TimePicker
                                                  use12Hours
                                                  format="h:mm a"
                                                  disabled={type === "view"}
                                                />
                                              </Form.Item>

                                              <Form.Item
                                                {...timeRestField}
                                                name={[timeName, "endTime"]}
                                                label="End"
                                                rules={[
                                                  {
                                                    required: true,
                                                    message:
                                                      "Please select end time",
                                                  },
                                                ]}
                                                className="col-span-2 mb-0"
                                              >
                                                <TimePicker
                                                  use12Hours
                                                  format="h:mm a"
                                                  disabled={type === "view"}
                                                />
                                              </Form.Item>

                                              <Form.Item
                                                {...timeRestField}
                                                name={[timeName, "price"]}
                                                label="Price"
                                                className=" mb-0"
                                                rules={[
                                                  {
                                                    required: true,
                                                    message:
                                                      "Please enter price",
                                                  },
                                                ]}
                                              >
                                                <InputNumber
                                                  style={{ width: "100%" }}
                                                  placeholder="৳"
                                                  disabled={type === "view"}
                                                />
                                              </Form.Item>

                                              <MainSeat
                                                form={form}
                                                mainName="additional_slots"
                                                mainIndex={name}
                                                timeName="slots"
                                                timeIndex={timeName}
                                                timeRestField={timeRestField}
                                                type={type}
                                              />

                                              {timeFields.length > 1 &&
                                                type !== "view" && (
                                                  <div
                                                    onClick={() =>
                                                      removeTime(timeName)
                                                    }
                                                    className="my-auto"
                                                  >
                                                    <RedDeleteIcon />
                                                  </div>
                                                )}
                                            </div>
                                          )
                                        )}

                                        {type !== "view" && (
                                          <button
                                            type="button"
                                            onClick={() => addTime()}
                                            className="flex items-center gap-2 text-accent text-sm font-medium mt-2"
                                          >
                                            <PlusOutlineIcon /> Another Slot
                                          </button>
                                        )}
                                      </>
                                    )}
                                  </Form.List>
                                </div>
                              </div>

                              {/* Show delete icon only if it's NOT the first item */}
                              {index !== 0 && type !== "view" && (
                                <div
                                  onClick={() => remove(name)}
                                  className="cursor-pointer mt-10"
                                >
                                  <RedDeleteIcon />
                                </div>
                              )}
                            </div>
                          ))}
                        </>
                      )}
                    </Form.List>
                  </div>
                </>
              ) : (
                <>
                  <Form.Item
                    name="contract_type"
                    className="mb-4"
                    rules={[
                      {
                        required: true,
                        message: "Please select a contract type",
                      },
                    ]}
                  >
                    <Radio.Group disabled={type === "view"}>
                      <div className="space-y-[26px]">
                        {contractTypes.map((el) => (
                          <div key={el.value} className="flex gap-2">
                            {/* Only radio here */}
                            <div className="flex items-start gap-2">
                              <Radio value={el.value}>
                                <div>
                                  <div className="font-semibold text-base text-header">
                                    {el.label}
                                  </div>
                                  {el.description && (
                                    <div className="text-sm text-body-text max-w-[300px]">
                                      {el.description}
                                    </div>
                                  )}
                                </div>
                              </Radio>
                            </div>

                            {/* ⬇ Render child form items outside of the radio */}
                            {selectedContractType === el.value && (
                              <div className="pl-6 grow flex justify-end">
                                {renderContractItems(el.value, type)}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </Radio.Group>
                  </Form.Item>
                </>
              )}

              {selectedCategory !== "call_center" && (
                <Divider className="bg-[#D7DFE9]" />
              )}

              {/* {selectedCategory !== "call_center" && ( */}
              <Form.Item
                name="specific_location_or_number_of_bondhu"
                className="mb-4"
                rules={[
                  {
                    required: true,
                    message: "Please select additional contents",
                  },
                ]}
              >
                <Radio.Group disabled={type === "view"}>
                  <div className="space-y-[26px]">
                    {additionalContents.map((el) => (
                      <div key={el.value} className="flex gap-2">
                        {/* Only radio here */}
                        <div className="flex items-start gap-2">
                          <Radio value={el.value}>
                            <div>
                              <div className="font-semibold text-base text-header">
                                {el.label}
                              </div>
                              {el?.description && (
                                <div className="text-sm text-body-text max-w-[300px]">
                                  {el?.description}
                                </div>
                              )}
                            </div>
                          </Radio>
                        </div>

                        {/* ⬇ Render child form items outside of the radio */}
                        {additionalContentsType === el.value && (
                          <div className="pl-6 grow flex justify-end">
                            {renderAdditionalContents(el.value, type)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </Radio.Group>
              </Form.Item>
              {/* )} */}

              <Divider className="bg-[#D7DFE9]" />

              {/* {selectedCategory !== "rooster" && (
                <div className="flex items-start justify-between w-full grow gap-3">
                  <div className="flex flex-col">
                    <span className="font-semibold text-base text-header">
                      Maximum Budget for The Job
                    </span>

                    <span className="text-sm text-body-text">
                      Project is broken into phases/milestones Payment released
                      upon milestone delivery
                    </span>
                  </div>
                  <div className={`grow flex justify-end`}>
                    <Form.Item
                      rules={[
                        {
                          required: true,
                          message: "Please enter maximum budget",
                        },
                        {
                          validator(_, value) {
                            if (
                              value === undefined ||
                              value === null ||
                              value === ""
                            ) {
                              return Promise.resolve();
                            }

                            const num = Number(value);

                            if (isNaN(num) || !Number.isInteger(num)) {
                              return Promise.reject(
                                new Error("Only integer number are allowed")
                              );
                            }

                            if (num <= 0) {
                              return Promise.reject(
                                new Error(
                                  "Maximum budget must be a positive number"
                                )
                              );
                            }

                            if (num < maxBudget) {
                              return Promise.reject(
                                new Error(
                                  "Maximum budget must be greater than contract price"
                                )
                              );
                            }

                            return Promise.resolve();
                          },
                        },
                      ]}
                      className="mb-2"
                      name="max_budget"
                      label="Amount"
                    >
                      <InputNumber
                        className="w-full"
                        placeholder="000"
                        size="large"
                        disabled={type === "view"}
                      />
                    </Form.Item>
                  </div>
                </div>
              )} */}

              <div className="flex items-start justify-between w-full grow gap-3">
                <div className="flex flex-col">
                  <span className="font-semibold text-base text-header min-w-[200px]">
                    Additional Questions
                  </span>

                  {selectedCategory === "call_center" && (
                    <span className="text-sm text-body-text">
                      Project is broken into phases/milestones Payment released
                      upon milestone delivery
                    </span>
                  )}
                </div>
                <div className={`grow w-full`}>
                  <Form.List initialValue={[""]} name="additional_questions">
                    {(fields, { add, remove }) => (
                      <div className="space-y-2">
                        {fields.map((field, index) => (
                          <div key={field.key} className="space-y-1.5">
                            <Form.Item
                              {...field}
                              label={`Q${index + 1}`}
                              name={[field.name]}
                              className="mb-2"
                              rules={[
                                {
                                  required: true,
                                  message: "Please enter your question",
                                },
                              ]}
                            >
                              <Input
                                className="min-w-[200px] !w-full"
                                placeholder="Write your question"
                                size="large"
                                disabled={type === "view"}
                                suffix={
                                  fields.length > 1 && type !== "view" ? (
                                    <button
                                      disabled={fields.length === 1}
                                      type="button"
                                      className="text-red-500"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        fields.length !== 1 &&
                                          remove(field.name);
                                      }}
                                    >
                                      <TrashIcon />
                                    </button>
                                  ) : null
                                }
                              />
                            </Form.Item>
                          </div>
                        ))}

                        {type !== "view" && (
                          <button
                            type="button"
                            onClick={() => add()}
                            className="flex items-center gap-2 text-link text-sm font-medium"
                          >
                            <PlusOutlineIcon />
                            Another Question
                          </button>
                        )}
                      </div>
                    )}
                  </Form.List>
                </div>
              </div>

              {selectedCategory !== "call_center" && (
                <div className="flex items-center">
                  <span className="font-semibold text-base text-header min-w-[200px]">
                    Allow file upload
                  </span>

                  <Form.Item
                    className="mb-0"
                    name="is_allow_file_upload"
                    valuePropName="checked"
                  >
                    <Switch disabled={type === "view"} />
                  </Form.Item>
                </div>
              )}

              {selectedCategory === "remote_work" &&
                selectedCategory !== "call_center" && (
                  <div className="flex items-center mt-2">
                    <span className="font-semibold text-base text-header min-w-[200px]">
                      Max number of files
                    </span>

                    <Form.Item
                      className="mb-0"
                      name="max_number_of_files"
                      validateFirst
                      rules={[
                        {
                          validator(_, value) {
                            if (
                              value === undefined ||
                              value === null ||
                              value === ""
                            ) {
                              return Promise.resolve();
                            }

                            const num = Number(value);

                            if (isNaN(num) || !Number.isInteger(num)) {
                              return Promise.reject(
                                new Error("Only integer numbers are allowed")
                              );
                            }

                            if (num < 1) {
                              return Promise.reject(
                                new Error("Value must be at least 1")
                              );
                            }

                            if (num > 100) {
                              return Promise.reject(
                                new Error("Value cannot exceed 100")
                              );
                            }

                            return Promise.resolve();
                          },
                        },
                      ]}
                    >
                      <InputNumber
                        placeholder="EX: 1"
                        size="large"
                        disabled={type === "view"}
                        // min={1}
                        // max={100}
                      />
                    </Form.Item>
                  </div>
                )}

              {selectedCategory !== "rooster" &&
                selectedCategory !== "remote_work" &&
                selectedCategory !== "call_center" && (
                  <>
                    <Divider className="bg-[#D7DFE9]" />
                    {selectedCategory === "survey" ? (
                      <Form.Item
                        name="survey_template_id"
                        label="Select Survey Template"
                        className="mb-4 pt-5 pb-5"
                        key={"survey_template_id_1"}
                        rules={[
                          {
                            required: true,
                            message: "Please select a template",
                          },
                        ]}
                      >
                        <Select
                          placeholder="Select"
                          size="large"
                          allowClear
                          showSearch
                          disabled={type === "view"}
                          options={getAllSurveyTemplateData?.data?.templates?.map(
                            (template) => ({
                              label: template.title,
                              value: template.id,
                            })
                          )}
                          suffixIcon={<SelectDropdownLgIcon />}
                          dropdownRender={(menu) => (
                            <>
                              {menu}

                              <Link to="/organizations/survey/templates/add">
                                <Button
                                  type="text"
                                  block
                                  size="middle"
                                  className="!text-primary !bg-[#FBF4FF]"
                                >
                                  <AddIcon /> Create New Template
                                </Button>
                              </Link>
                            </>
                          )}
                        />
                      </Form.Item>
                    ) : (
                      <Form.Item
                        name="survey_template_id"
                        label="Select Template"
                        key={"survey_template_id_2"}
                        className="mb-4"
                        // rules={[
                        //   {
                        //     required: true,
                        //     message: "Please select a template",
                        //   },
                        // ]}
                      >
                        <Select
                          placeholder="Select"
                          size="large"
                          allowClear
                          showSearch
                          disabled={type === "view"}
                          options={getAllSurveyTemplateData?.data?.templates?.map(
                            (template) => ({
                              label: template.title,
                              value: template.id,
                            })
                          )}
                          suffixIcon={<SelectDropdownLgIcon />}
                          dropdownRender={(menu) => (
                            <>
                              {menu}

                              <Link to="/organizations/survey/templates/add">
                                <Button
                                  type="text"
                                  block
                                  size="middle"
                                  className="!text-primary !bg-[#FBF4FF]"
                                >
                                  <AddIcon /> Create New Template
                                </Button>
                              </Link>
                            </>
                          )}
                        />
                      </Form.Item>
                    )}

                    {/* {selectedCategory === "kyc" && ( */}
                    {/* <Form.Item
                    name="template"
                    label="Select survey templates"
                    className="mb-4"
                    // rules={[
                    //   {
                    //     required: true,
                    //     message: "Please select a template",
                    //   },
                    // ]}
                  >
                    <Select
                      placeholder="Select"
                      size="large"
                      allowClear
                      showSearch
                      // options={categoryList}
                      suffixIcon={<SelectDropdownLgIcon />}
                      mode="multiple"
                      disabled={type === "view"}
                    />
                  </Form.Item> */}
                    {/* )} */}

                    {selectedCategory !== "remote_work" && (
                      <div className="space-y-[26px]">
                        <div className="flex gap-2">
                          {/* Only radio here */}
                          <div className="flex items-start gap-2">
                            <div>
                              <div className="font-semibold text-base text-header">
                                {selectedCategory === "survey"
                                  ? "Survey"
                                  : "Job"}{" "}
                                Location
                              </div>
                              {/* <div className="text-sm text-body-text max-w-[300px]">
                            Project is broken into phases/milestones Payment
                            released upon milestone delivery
                          </div> */}
                            </div>
                          </div>

                          {/* ⬇ Render child form items outside of the radio */}

                          <div className="pl-6 grow flex justify-end">
                            <div>
                              <Form.List
                                name="survey_location"
                                // initialValue={[{}]}
                              >
                                {(fields, { add, remove }) => (
                                  <>
                                    {fields.map(
                                      ({ key, name, ...restField }, index) => {
                                        const current =
                                          form.getFieldValue(
                                            "survey_location"
                                          )?.[name];
                                        const updatedData = {
                                          office_name: current?.location,
                                          radius: current?.radius,
                                        };

                                        return (
                                          <>
                                            <div className="flex items-start gap-3">
                                              <Form.Item
                                                rules={[
                                                  {
                                                    required: true,
                                                    message:
                                                      "Please enter a location",
                                                  },
                                                ]}
                                                className="mb-3"
                                                name={[name, "location"]}
                                                label="Add Location"
                                              >
                                                <Input
                                                  className="min-w-[200px]"
                                                  placeholder="Select"
                                                  size="large"
                                                  disabled
                                                />
                                              </Form.Item>

                                              <Form.Item
                                                rules={[
                                                  {
                                                    required: true,
                                                    message:
                                                      "Please select number of bondhu",
                                                  },
                                                  {
                                                    validator(_, value) {
                                                      if (
                                                        value === undefined ||
                                                        value === null ||
                                                        value === ""
                                                      ) {
                                                        return Promise.resolve();
                                                      }

                                                      const num = Number(value);

                                                      if (
                                                        isNaN(num) ||
                                                        !Number.isInteger(num)
                                                      ) {
                                                        return Promise.reject(
                                                          new Error(
                                                            "Only integer number are allowed"
                                                          )
                                                        );
                                                      }

                                                      if (num <= 0) {
                                                        return Promise.reject(
                                                          new Error(
                                                            "Value must be a positive number"
                                                          )
                                                        );
                                                      }

                                                      return Promise.resolve();
                                                    },
                                                  },
                                                ]}
                                                className="mb-2"
                                                name={[
                                                  name,
                                                  "number_of_bondhu",
                                                ]}
                                                label="Number of Bondhu"
                                              >
                                                <Input
                                                  className="w-[150px]"
                                                  placeholder="#"
                                                  size="large"
                                                  disabled={type === "view"}
                                                />
                                              </Form.Item>

                                              <Form.Item
                                                className="sr-only"
                                                name={[name, "radius"]}
                                                label="Radius"
                                              >
                                                <Input
                                                  className="min-w-[150px]"
                                                  placeholder="#"
                                                  size="large"
                                                  disabled
                                                />
                                              </Form.Item>

                                              {type !== "view" && (
                                                <div
                                                  className="cursor-pointer mt-10"
                                                  onClick={() => {
                                                    setSelectedLocationIndex(
                                                      index
                                                    );
                                                    handleOpen({
                                                      type: "LOCATION",
                                                      data: updatedData,
                                                    });
                                                  }}
                                                >
                                                  <RoundEditIcon currentColor="#3D4A5C" />
                                                </div>
                                              )}

                                              {fields.length > 1 &&
                                                type !== "view" && (
                                                  <div
                                                    className="cursor-pointer mt-10"
                                                    onClick={() => remove(name)}
                                                  >
                                                    <RedDeleteIcon />
                                                  </div>
                                                )}
                                            </div>
                                          </>
                                        );
                                      }
                                    )}

                                    {type !== "view" && (
                                      <button
                                        type="button"
                                        className="flex items-center gap-2 text-link text-sm font-medium"
                                        onClick={() => add()}
                                      >
                                        <PlusOutlineIcon />
                                        Another Location
                                      </button>
                                    )}
                                  </>
                                )}
                              </Form.List>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
            </div>

            <BalanceCard type={type} />
          </div>
        </Form>
      )}
    </SidebarLayout>
  );
}

const job_category = [
  {
    label: "Survey",
    value: "survey",
  },
  // TODO:
  // {
  //   label: "Rooster",
  //   value: "rooster",
  // },
  {
    label: "Call Center",
    value: "call_center",
  },
  {
    label: "Remote Work",
    value: "remote_work",
  },
];
const job_visibility = [
  {
    label: "Public",
    value: "public",
  },
  {
    label: "Private",
    value: "private",
  },
];

const contractTypes = [
  {
    label: "Fixed-Term Contract",
    value: "fixed_term",
    description: "",
    // description: "Duration-based (e.g., 3 months, 6 months, 12 months)",
  },
  {
    label: "Hourly Contract",
    value: "hourly",
    // description:
    //   "Pay based on hours worked You can track time (using timers or manual logs)",
  },
  {
    label: "Open for Bids",
    value: "open_bid",
    // description: "Freelancers will propose their price and delivery timeline.",
  },
  // TODO:
  // {
  //   label: "Milestone-Based Contract",
  //   value: "milestone",
  //   description:
  //     "Project is broken into phases/milestones Payment released upon milestone delivery",
  // },
];

type MainSeatProps = {
  timeRestField: any;
  mainName: string;
  timeName: string;
  mainIndex: number;
  timeIndex: number;
  form: any;
  type?: "view";
};

const MainSeat = ({
  timeRestField,
  mainName,
  mainIndex,
  timeName,
  timeIndex,
  form,
  type,
}: MainSeatProps) => {
  const isSeatActive = Form.useWatch(
    [mainName, mainIndex, timeName, timeIndex, "is_seat_active"],
    form
  );

  useEffect(() => {
    if (!isSeatActive) {
      const currentSeat = form.getFieldValue([
        mainName,
        mainIndex,
        timeName,
        timeIndex,
        "seat",
      ]);

      if (!currentSeat) {
        form.setFieldsValue({
          [mainName]: {
            [mainIndex]: {
              [timeName]: {
                [timeIndex]: {
                  seat: 1,
                },
              },
            },
          },
        });
      }
    }
  }, [form, isSeatActive, mainIndex, mainName, timeIndex, timeName]);

  return (
    <Form.Item
      {...timeRestField}
      name={[timeIndex, "seat"]}
      className="col-span-2 mb-0"
      label={
        <div className="flex items-center">
          <div>Seat</div>
          <div className="ml-16">
            <Form.Item
              {...timeRestField}
              name={[timeIndex, "is_seat_active"]}
              valuePropName="checked"
              className="mb-0"
            >
              <Switch disabled={type === "view"} />
            </Form.Item>
          </div>
        </div>
      }
      rules={[
        {
          required: true,
          message: "Please enter seat",
        },
      ]}
    >
      <InputNumber
        placeholder="#"
        // disabled={!isSeatActive}
        disabled={type === "view" || !isSeatActive}
        style={{ width: "100%" }}
      />
    </Form.Item>
  );
};
